import { Check, ChevronDown, SparklesIcon } from "lucide-react";
import React, { useEffect, useState, useCallback, useId, useRef } from "react";

import { CaseStatus } from "../../../api/cases";
import { cn } from "../../../lib/utils";

import CaseStatusBadge, {
  STATUS_OPTIONS,
} from "../../../components/ui/casestatusbadge/CaseStatusBadge";
import ErrorDisplayV2 from "../../../components/ui/error/v2";
import Heading from "../../../components/ui/heading/Heading";
import { CustomLoadingSpinner } from "../../../components/ui/spinners/LoadingSpinner";

import { isAIInsightsEnabled } from "../../configs/logic/Slice";
import FeatureGateActionButton from "../../featureFlags/components/FeatureGateActionButton";

import { updateCaseFields, validateStatus } from "../logic/validations";

import { KeyStakeHolders } from "../templates";
import { CaseOverviewCard } from "./CaseOverviewCard";

import {
  selectCaseHeader,
  selectCaseHeaderLoadingStatus,
  selectCaseHeaderError,
  selectCaseHeaderUpdateError,
  selectIsEditMode,
  selectCaseHeaderIsUpdating,
  selectEditableField,
  setEditMode,
  setEditableField,
  clearUpdateError,
} from "../logic/Slice";

import { fetchCaseInfo, updateCase } from "../../../thunks/Case";

import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { Can } from "../../auth/components/Can";
import { Permission } from "../../auth/logic/rbac";

interface CaseHeaderInfoProps {
  caseId: string;
  className?: string;
  onClickAIInsights: () => void;
}

interface CaseHeaderStatusSelectProps {
  status: CaseStatus;
  onChange: (status: CaseStatus) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  className?: string;
  disabled?: boolean;
}

const CASE_STATUS_DESCRIPTION_MAP: Record<CaseStatus, string> = {
  [CaseStatus.OPEN]: "Newly created and awaiting fact-finding.",
  [CaseStatus.IN_PROGRESS]: "Investigation is actively underway.",
  [CaseStatus.DECLARED_AS_NO_FRAUD]:
    "The FIC Committee's final decision is No Fraud.",
  [CaseStatus.DECLARED_AS_FRAUD]:
    "The FIC Committee's final decision is Fraud.",
  [CaseStatus.DECLARED_AS_ATTEMPTED_FRAUD]:
    "The FIC Committee's final decision is Attempted Fraud.",
  [CaseStatus.DEACTIVATED]: "Superseded by a re-examination case.",
  [CaseStatus.CLOSED]: "Final or statistical closure.",
  [CaseStatus.REJECTED]: "Rejected before the investigation proceeds.",
};

const CaseHeaderStatusSelect: React.FC<CaseHeaderStatusSelectProps> = ({
  status,
  onChange,
  onFocus,
  onBlur,
  className,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const listboxId = useId();

  useEffect(() => {
    if (!open) return;
    const onDocumentMouseDown = (e: MouseEvent) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onDocumentKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", onDocumentMouseDown);
    document.addEventListener("keydown", onDocumentKeyDown);
    return () => {
      document.removeEventListener("mousedown", onDocumentMouseDown);
      document.removeEventListener("keydown", onDocumentKeyDown);
    };
  }, [open]);

  const handleWrapperBlur: React.FocusEventHandler<HTMLDivElement> = (e) => {
    const next = e.relatedTarget as Node | null;
    if (wrapperRef.current && next && wrapperRef.current.contains(next)) return;
    setOpen(false);
    onBlur?.();
  };

  return (
    <div
      ref={wrapperRef}
      className={cn("relative inline-block", className)}
      onBlur={handleWrapperBlur}
    >
      <button
        ref={buttonRef}
        type="button"
        className={cn(
          "relative inline-flex w-fit items-center rounded-md hover:brightness-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-0 focus-visible:ring-primary-500",
          disabled && "cursor-not-allowed opacity-60 hover:brightness-100",
        )}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        onFocus={() => onFocus?.()}
        onClick={() => {
          if (disabled) return;
          setOpen((v) => !v);
        }}
      >
        <CaseStatusBadge status={status} className="pr-6" />
        <ChevronDown className="absolute right-2 top-1/2 h-3 w-3 -translate-y-1/2 text-current pointer-events-none" />
      </button>

      {open && (
        <div
          id={listboxId}
          role="listbox"
          className="absolute left-0 mt-1 z-50 w-80 rounded-lg border border-gray-200 bg-white p-2 shadow-lg"
        >
          <div className="flex flex-col gap-0.5">
            {STATUS_OPTIONS.map((statusOption) => (
              <button
                key={statusOption}
                type="button"
                role="option"
                aria-selected={statusOption === status}
                className={cn(
                  "w-full rounded-md px-2 py-2 text-left hover:bg-[#F3F4F6] focus:outline-none focus:ring-2 focus:ring-offset-0 focus:ring-primary-500",
                  statusOption === status && "bg-[#F9FAFB]",
                )}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onChange(statusOption);
                  setOpen(false);
                }}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <CaseStatusBadge
                      status={statusOption}
                      className="border-0"
                    />
                    <p className="mt-1 text-xs leading-4 text-gray-500">
                      {CASE_STATUS_DESCRIPTION_MAP[statusOption]}
                    </p>
                  </div>
                  {statusOption === status && (
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-gray-600" />
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const CaseHeaderInfo: React.FC<CaseHeaderInfoProps> = ({
  caseId,
  className,
  onClickAIInsights
}) => {

  const dispatch = useAppDispatch();

  const summaryData = useAppSelector(selectCaseHeader);
  const isLoading = useAppSelector(selectCaseHeaderLoadingStatus) === "pending";
  const isSaving = useAppSelector(selectCaseHeaderIsUpdating);
  const fetchError = useAppSelector(selectCaseHeaderError);
  const updateError = useAppSelector(selectCaseHeaderUpdateError);
  const isEditMode = useAppSelector(selectIsEditMode);
  const editableField = useAppSelector(selectEditableField);

  const isEditingCaseStatus = editableField === updateCaseFields.STATUS;

  const [caseStatus, setCaseStatus] = useState<CaseStatus>(
    summaryData?.status || CaseStatus.OPEN
  );

  const [caseStatusError, setCaseStatusError] = useState<string | null>(null);

  useEffect(() => {
    if (summaryData?.status) {
      setCaseStatus(summaryData.status);
    }
  }, [summaryData?.status]);

  const startEdit = useCallback((fieldName: string) => {
    if (isSaving) return;
    dispatch(setEditMode(true));
    dispatch(setEditableField(fieldName));
  }, [dispatch, isSaving]);

  const cancelEdit = useCallback(() => {
    dispatch(clearUpdateError());
    dispatch(setEditableField(""));
    dispatch(setEditMode(false));
  }, [dispatch]);

  const onStatusChange = async (status: CaseStatus) => {

    if (status === summaryData?.status) return;
    const error = validateStatus(status);

    setCaseStatusError(error);
    setCaseStatus(status);

    if (error || !summaryData?.case_id) return;

    const resultAction = await dispatch(
      updateCase({
        caseId: summaryData.case_id,
        request: { status }
      })
    );

    if (updateCase.fulfilled.match(resultAction)) {

      cancelEdit();

    } else {
      console.error("Update failed:", resultAction.payload);
    }
  };

  const handleRetry = useCallback(() => {
    dispatch(clearUpdateError());
    dispatch(fetchCaseInfo({ case_id: caseId }));
  }, [dispatch, caseId]);


  if (fetchError) {
    return (
      <ErrorDisplayV2
        errorMessage={fetchError}
        onRetry={() => dispatch(fetchCaseInfo({ case_id: caseId }))}
        className="mb-6"
      />
    );
  }

  if (updateError) {
    return (
      <ErrorDisplayV2
        title="Sorry, something went wrong and we couldn't save your changes. Please refresh and try again."
        errorMessage={updateError}
        onRetry={handleRetry}
        className="mb-6"
      />
    );
  }

  if (isLoading && !isEditMode) {
    return (
      <CustomLoadingSpinner
        title="Loading Case Info..."
        subtitle="Fetching investigation overview, stakeholders and additional details."
        minHeight="min-h-[400px]"
      />
    );
  }

  if (!summaryData) {
    return (
      <div
        className={cn(
          "p-4 sm:p-6 bg-card border-b border-gray-200 text-center text-muted-foreground",
          className
        )}
      >
        Case summary information is not available.
      </div>
    );
  }

  return (
    <div>

      <div className="grid grid-cols-1 md:grid-cols-[1fr_331px] gap-6">

        <div className="bg-white rounded-xl shadow-overviewCard overflow-hidden min-w-0">

          <div className="flex justify-between items-center px-4 py-3 bg-gray-50 rounded-t-xl">

            <div className="flex gap-2 items-center">

              <Heading level={1} className="text-lg font-semibold text-gray-900">
                Overview
              </Heading>

              <div className="flex flex-col">

                <CaseHeaderStatusSelect
                  className="w-fit"
                  disabled={isEditingCaseStatus && isSaving}
                  status={caseStatus}
                  onFocus={() => startEdit(updateCaseFields.STATUS)}
                  onBlur={() => {
                    if (isEditingCaseStatus && !isSaving) {
                      cancelEdit();
                    }
                  }}
                  onChange={onStatusChange}
                />

                {caseStatusError && (
                  <p className="mt-1 text-xs text-red-600">
                    {caseStatusError}
                  </p>
                )}

              </div>

              {isEditingCaseStatus && isSaving && (
                <span className="text-xs text-gray-500 italic">
                  Saving...
                </span>
              )}

            </div>

            <div className="flex items-center space-x-2">
              <Can perform={Permission.ACCESS_CASE_RIGHT_SIDEBAR} variant="hide">
                <FeatureGateActionButton
                  text="AI Insights"
                  icon={<SparklesIcon width={16} height={16} />}
                  onClick={() => onClickAIInsights()}
                  selector={isAIInsightsEnabled}
                  disabledMessage="Advanced AI capabilities disabled due to absence of tenant data integrations."
                />
              </Can>
            </div>

          </div>

          <div className=" px-4 p-2 bg-white">
            <CaseOverviewCard />
          </div>

        </div>

        <div className="bg-white rounded-xl shadow-md p-3 space-y-6">
          <KeyStakeHolders />
        </div>

      </div>

    </div>
  );
};
